import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { LineChart as LineChartIcon, TrendingUp, TrendingDown, Target, ExternalLink } from "lucide-react";

const TYPE_LABEL = { stock: "Acciones", fund: "Fondos", crypto: "Cripto" };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatMoney(value, currency) {
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "USD" }).format(value || 0);
  } catch {
    return `${(value || 0).toFixed(2)} ${currency || ""}`;
  }
}

export default function PublicWatchlist() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/watchlist/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "Enlace inválido o revocado" : "Error cargando el enlace"));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
        <div className="max-w-md text-center card-surface p-10">
          <div className="text-2xl font-display text-[#0A110E] mb-2">Ups</div>
          <div className="text-sm text-[#5F7368]">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-sm text-[#5F7368]">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.1em] font-bold text-[#8A9992] inline-flex items-center gap-2">
              <LineChartIcon className="w-3.5 h-3.5" /> Watchlist compartida
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#0A110E]" data-testid="public-watchlist-title">
              {data.owner_first_name ? `Watchlist de ${data.owner_first_name}` : "Watchlist"}
            </h1>
            <p className="text-sm text-[#5F7368] mt-1">
              {data.count} activo{data.count === 1 ? "" : "s"} · Precios en tiempo cercano al real
            </p>
          </div>
          <a
            href="/"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#1C7C54] hover:underline"
          >
            Patrimonio <ExternalLink className="w-3 h-3" />
          </a>
        </header>

        {data.items.length === 0 ? (
          <div className="card-surface p-10 text-center text-sm text-[#8A9992]">
            Esta watchlist no tiene activos por ahora.
          </div>
        ) : (
          <div className="card-surface overflow-hidden" data-testid="public-watchlist-list">
            {data.items.map((it, idx) => {
              const up = (it.change_pct ?? 0) >= 0;
              return (
                <div
                  key={`${it.name}-${idx}`}
                  data-testid={`public-watchlist-row-${idx}`}
                  className="grid grid-cols-12 gap-2 px-4 py-4 border-b border-[#EBEBEA] last:border-0 items-center"
                >
                  <div className="col-span-12 sm:col-span-5 min-w-0">
                    <div className="font-semibold text-[#0A110E] truncate">{it.name}</div>
                    <div className="text-[11px] text-[#8A9992]">
                      {TYPE_LABEL[it.type] || it.type}
                      {it.ticker && ` · ${it.ticker.toUpperCase()}`}
                      {it.isin && ` · ${it.isin}`}
                    </div>
                    {(it.target_price_up || it.target_price_down) && (
                      <div className="mt-1 inline-flex items-center gap-2 text-[11px] text-[#8B6B1F]">
                        <Target className="w-3 h-3" />
                        {it.target_price_up && <span>↑ {formatMoney(it.target_price_up, it.currency)}</span>}
                        {it.target_price_down && <span>↓ {formatMoney(it.target_price_down, it.currency)}</span>}
                      </div>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-4 text-right sm:text-right">
                    <div className="font-display text-lg font-semibold tabular-nums text-[#0A110E]">
                      {formatMoney(it.current_price, it.currency)}
                    </div>
                  </div>
                  <div className="col-span-6 sm:col-span-3 text-right">
                    {it.change_pct != null ? (
                      <span className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-xs font-semibold ${
                        up ? "bg-[#1C7C54]/10 text-[#1C7C54]" : "bg-[#D95D39]/10 text-[#D95D39]"
                      }`}>
                        {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        {up ? "+" : ""}{it.change_pct.toFixed(2)}%
                      </span>
                    ) : (
                      <span className="text-[11px] text-[#8A9992]">sin datos</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <footer className="text-center text-[11px] text-[#8A9992] pt-6">
          Enlace público de solo lectura. No se comparten cantidades ni saldos.
        </footer>
      </div>
    </div>
  );
}
